/**
 * StudyGroupFormModal — Модальное окно создания/редактирования учебной группы
 *
 * Поля: name, subject_id, teacher_user_id, duration_minutes,
 *        max_students, schedules (динамический список), notes
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  TextInput,
  Textarea,
  Select,
  NumberInput,
  Button,
  Group,
  Stack,
  Alert,
  Box,
  Text,
  ActionIcon,
  Paper,
} from '@mantine/core';
import { TimePicker, DatePickerInput } from '@mantine/dates';
import {
  IconAlertCircle,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import tokens from '../../../theme/designTokens';
import {
  createStudyGroup,
  updateStudyGroup,
} from '../../../services/api/studyGroupsService';

const DAY_OPTIONS = [
  { value: '0', label: 'Понедельник' },
  { value: '1', label: 'Вторник' },
  { value: '2', label: 'Среда' },
  { value: '3', label: 'Четверг' },
  { value: '4', label: 'Пятница' },
  { value: '5', label: 'Суббота' },
  { value: '6', label: 'Воскресенье' },
];

const DURATION_OPTIONS = [
  { value: '30', label: '30 мин' },
  { value: '45', label: '45 мин' },
  { value: '60', label: '1 час' },
  { value: '75', label: '1 ч 15 мин' },
  { value: '90', label: '1 ч 30 мин' },
  { value: '105', label: '1 ч 45 мин' },
  { value: '120', label: '2 часа' },
];

/**
 * Компонент одного слота расписания
 */
const ScheduleSlotItem = ({ slot, index, onChange, onRemove, canRemove }) => {
  return (
    <Paper p="xs" radius={12} style={{ border: `1px solid ${tokens.colors.neutral[3]}` }}>
      <Group gap="sm" align="flex-end">
        <Select
          label={index === 0 ? 'День недели' : undefined}
          placeholder="День"
          data={DAY_OPTIONS}
          value={slot.day_of_week !== null ? String(slot.day_of_week) : null}
          onChange={(val) => onChange(index, 'day_of_week', val !== null ? parseInt(val) : null)}
          style={{ flex: 1 }}
          radius="xl"
        />
        <TimePicker
          label={index === 0 ? 'Время начала' : undefined}
          placeholder="--:--"
          value={slot.start_time || ''}
          onChange={(val) => onChange(index, 'start_time', val)}
          withDropdown
          hoursStep={1}
          minutesStep={5}
          style={{ flex: 1 }}
        />
        <DatePickerInput
          label={index === 0 ? 'Дата начала' : undefined}
          placeholder="Дата"
          value={slot.start_date ? new Date(slot.start_date) : null}
          onChange={(val) => {
            if (!val) {
              onChange(index, 'start_date', null);
              return;
            }
            const d = new Date(val);
            if (!isNaN(d.getTime())) {
              const yyyy = d.getFullYear();
              const mm = String(d.getMonth() + 1).padStart(2, '0');
              const dd = String(d.getDate()).padStart(2, '0');
              onChange(index, 'start_date', `${yyyy}-${mm}-${dd}`);
            }
          }}
          clearable
          radius="xl"
          locale="ru"
          valueFormat="DD.MM.YYYY"
          style={{ flex: 1 }}
        />
        {canRemove && (
          <ActionIcon
            variant="subtle"
            color="red"
            onClick={() => onRemove(index)}
            mb={index === 0 ? 0 : 2}
          >
            <IconTrash size={16} />
          </ActionIcon>
        )}
      </Group>
    </Paper>
  );
};

const StudyGroupFormModal = ({ opened, onClose, onSuccess, group, organizationId }) => {
  const isEdit = !!group;

  // Справочники
  const [subjects, setSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);

  // Форма
  const [name, setName] = useState('');
  const [subjectId, setSubjectId] = useState(null);
  const [teacherUserId, setTeacherUserId] = useState(null);
  const [durationMinutes, setDurationMinutes] = useState('60');
  const [maxStudents, setMaxStudents] = useState(null);
  const [notes, setNotes] = useState('');
  const [schedules, setSchedules] = useState([{ day_of_week: null, start_time: '', start_date: null }]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Загрузка справочников
  const loadReferenceData = useCallback(async () => {
    try {
      const [subjectsRes, teachersRes] = await Promise.all([
        fetch('/api/subjects', { credentials: 'include' }).then((r) => r.json()),
        fetch('/api/users?role=TEACHER', { credentials: 'include' }).then((r) => r.json()),
      ]);
      setSubjects(
        (subjectsRes || []).map((s) => ({
          value: String(s.id),
          label: s.name,
        }))
      );
      const teacherItems = teachersRes?.items || teachersRes || [];
      setTeachers(
        teacherItems.map((t) => ({
          value: String(t.id),
          label: [t.last_name, t.first_name].filter(Boolean).join(' ') || t.email,
        }))
      );
    } catch (err) {
      console.error('Failed to load reference data:', err);
    }
  }, []);

  useEffect(() => {
    if (opened) {
      loadReferenceData();
    }
  }, [opened, loadReferenceData]);

  // Инициализация формы при открытии
  useEffect(() => {
    if (opened) {
      if (isEdit && group) {
        setName(group.name || '');
        setSubjectId(group.subject_id ? String(group.subject_id) : null);
        setTeacherUserId(group.teacher_user_id ? String(group.teacher_user_id) : null);
        setDurationMinutes(String(group.duration_minutes || 60));
        setMaxStudents(group.max_students || null);
        setNotes(group.notes || '');
        setSchedules(
          (group.schedules || []).map((s) => ({
            day_of_week: s.day_of_week,
            start_time: s.start_time ? s.start_time.substring(0, 5) : '',
            start_date: s.start_date || null,
          }))
        );
      } else {
        setName('');
        setSubjectId(null);
        setTeacherUserId(null);
        setDurationMinutes('60');
        setMaxStudents(null);
        setNotes('');
        setSchedules([{ day_of_week: null, start_time: '', start_date: null }]);
      }
      setError(null);
    }
  }, [opened, isEdit, group]);

  const handleScheduleChange = (index, field, value) => {
    setSchedules((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleAddSlot = () => {
    setSchedules((prev) => [...prev, { day_of_week: null, start_time: '', start_date: null }]);
  };

  const handleRemoveSlot = (index) => {
    setSchedules((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    setError(null);

    // Валидация
    if (!name.trim()) {
      setError('Укажите название группы');
      return;
    }

    const validSchedules = schedules.filter(
      (s) => s.day_of_week !== null && s.start_time
    );
    if (validSchedules.length === 0) {
      setError('Добавьте хотя бы один слот расписания');
      return;
    }

    const payload = {
      name: name.trim(),
      subject_id: subjectId ? parseInt(subjectId) : null,
      teacher_user_id: teacherUserId ? parseInt(teacherUserId) : null,
      duration_minutes: parseInt(durationMinutes),
      max_students: maxStudents || null,
      notes: notes.trim() || null,
      schedules: validSchedules.map((s) => ({
        day_of_week: s.day_of_week,
        start_time: s.start_time,
        start_date: s.start_date || null,
      })),
    };

    setLoading(true);
    try {
      if (isEdit) {
        await updateStudyGroup(organizationId, group.id, payload);
        notifications.show({
          title: 'Успешно',
          message: 'Группа обновлена',
          color: 'green',
        });
      } else {
        await createStudyGroup(organizationId, payload);
        notifications.show({
          title: 'Успешно',
          message: 'Группа создана',
          color: 'green',
        });
      }
      onSuccess();
    } catch (err) {
      console.error('Failed to save study group:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={isEdit ? 'Редактировать группу' : 'Создать учебную группу'}
      size="lg"
      centered
    >
      <Stack gap="md">
        {error && (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            {error}
          </Alert>
        )}

        <TextInput
          label="Название группы"
          placeholder="Например: Математика 1 класс"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          radius="xl"
        />

        <Group grow>
          <Select
            label="Предмет"
            placeholder="Выберите предмет"
            data={subjects}
            value={subjectId}
            onChange={setSubjectId}
            clearable
            searchable
            radius="xl"
          />

          <Select
            label="Педагог"
            placeholder="Выберите педагога"
            data={teachers}
            value={teacherUserId}
            onChange={setTeacherUserId}
            clearable
            searchable
            radius="xl"
          />
        </Group>

        <Group grow>
          <Select
            label="Продолжительность"
            data={DURATION_OPTIONS}
            value={durationMinutes}
            onChange={setDurationMinutes}
            required
            radius="xl"
          />

          <NumberInput
            label="Макс. учеников"
            placeholder="Без ограничения"
            value={maxStudents || ''}
            onChange={(val) => setMaxStudents(val || null)}
            min={1}
            radius="xl"
          />
        </Group>

        {/* Расписание */}
        <Box>
          <Text size="sm" fw={500} mb="xs">
            Расписание
          </Text>
          <Stack gap="xs">
            {schedules.map((slot, index) => (
              <ScheduleSlotItem
                key={index}
                slot={slot}
                index={index}
                onChange={handleScheduleChange}
                onRemove={handleRemoveSlot}
                canRemove={schedules.length > 1}
              />
            ))}
          </Stack>
          <Button
            variant="light"
            size="xs"
            mt="xs"
            leftSection={<IconPlus size={14} />}
            onClick={handleAddSlot}
          >
            Добавить слот
          </Button>
        </Box>

        <Textarea
          label="Заметки"
          placeholder="Дополнительная информация"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          autosize
          minRows={2}
          radius="xl"
        />

        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose}>
            Отмена
          </Button>
          <Button onClick={handleSubmit} loading={loading}>
            {isEdit ? 'Сохранить' : 'Создать'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default StudyGroupFormModal;
